import React from "react";
import { CustomCellRendererProps } from "ag-grid-react";
import { Box } from "@mui/material";
import { Typography } from "@platform/design-system";

export interface BenefitClass {
  id?: string;
  identifier?: string;
  name: string;
}

/**
 * Normalise the raw cell value into a typed array of BenefitClass objects.
 *
 * Handles every shape that can arrive at runtime:
 *  - `BenefitClass[]`  — pass-through
 *  - `string`          — comma-separated names (AG Grid can coerce arrays to strings)
 *  - `undefined | null | ""` — empty
 */
const normalizeValue = (raw: unknown): BenefitClass[] => {
  if (Array.isArray(raw)) {
    return raw as BenefitClass[];
  }
  if (typeof raw === "string" && raw.trim().length > 0) {
    // Best-effort recovery: turn "Class A, Class B" into renderable objects
    return raw.split(",").map((s) => ({ name: s.trim() }));
  }
  return [];
};

/**
 * Cell renderer for eligibility classes
 * Displays comma-separated list of selected class names
 *
 * Handles both `{ id, name }` (backend response) and `{ identifier, name }`
 * (canonical frontend type) shapes, as well as string fallbacks.
 *
 * When the field is required (isRequired) and no classes are selected,
 * the "Click to select" text is rendered in the error color to match
 * the cell-level required-missing styling applied by cellClassRules.
 */
export const EligibilityClassesRenderer: React.FC<CustomCellRendererProps> = (
  props,
) => {
  const classes = normalizeValue(props.value as unknown);

  // Handle empty state — render an empty clickable area
  if (classes.length === 0) {
    return (
      <Box
        sx={{
          cursor: "pointer",
          width: "100%",
          height: "100%",
        }}
      />
    );
  }

  // Extract names and join with commas
  const names = classes
    .map((cls: BenefitClass) => cls.name)
    .filter(Boolean)
    .join(", ");

  if (!names) {
    return (
      <Box
        sx={{
          cursor: "pointer",
          width: "100%",
          height: "100%",
        }}
      />
    );
  }

  return (
    <Box sx={{ cursor: "pointer", py: 0.5 }}>
      <Typography variant="body-medium">{names}</Typography>
    </Box>
  );
};
